'use client';

import { useState, useEffect, FormEvent } from 'react';
import { FaUser, FaPhone, FaEnvelope } from 'react-icons/fa';
import ReCaptchaEnterprise, { resetEnterpriseCaptcha } from '@/components/ReCaptchaEnterprise';
import { getApiUrl, getCacheHeaders } from '@/lib/site-config';
import { RECAPTCHA_CONFIG } from '@/config/recaptcha';
import { RODO_SECTIONS, RODO_TITLE } from '@/lib/rodo-content';

// Interaktywna wyspa: formularz zapisu na termin + modal RODO + reCAPTCHA.
// Wydzielona z dawnej client-page /event/[id], by treść i schema mogły renderować się serwerowo (SEO/GEO).

export interface EventRegistrationProps {
  eventId: number;
  title: string;
  startDate?: string;
  endDate?: string;
  time?: string;
  price?: string;
  hasSeats: boolean;
}

interface RegistrationFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  rodoConsent: boolean;
}

interface FormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  rodoConsent?: string;
}

const DEFAULT_RODO = {
  title: RODO_TITLE,
  content: RODO_SECTIONS.map((s) => `<h4>${s.title}</h4>\n${s.content}`).join('\n\n'),
};

export default function EventRegistration({
  eventId,
  title,
  startDate,
  endDate,
  time,
  price,
  hasSeats,
}: EventRegistrationProps) {
  const [formData, setFormData] = useState<RegistrationFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    rodoConsent: false,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [recaptchaToken, setRecaptchaToken] = useState<string | null>(null);
  const [recaptchaError, setRecaptchaError] = useState<string | null>(null);
  const [showRodoDetails, setShowRodoDetails] = useState(false);
  const [rodoContent, setRodoContent] = useState<{ title: string; content: string }>(DEFAULT_RODO);

  // Pobranie aktualnej treści RODO z API (fallback: stała z @/lib/rodo-content)
  useEffect(() => {
    const fetchRodoData = async () => {
      try {
        const timestamp = Date.now();
        const baseUrl = getApiUrl('rodo');
        const separator = baseUrl.includes('?') ? '&' : '?';
        const apiUrl = `${baseUrl}${separator}t=${timestamp}`;
        const response = await fetch(apiUrl, { method: 'GET', headers: { ...getCacheHeaders() } });
        if (response.ok) {
          const data = await response.json();
          if (data && data.success && data.data && Array.isArray(data.data) && data.data.length > 0) {
            const combinedContent = data.data
              .map((section: { title: string; content: string }) => `<h4>${section.title}</h4>\n${section.content}`)
              .join('\n\n');
            setRodoContent({ title: 'RODO – Klauzula informacyjna', content: combinedContent });
          }
        }
      } catch (error) {
        console.error('Błąd podczas pobierania danych RODO:', error);
      }
    };
    fetchRodoData();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: inputValue }));
    if (formErrors[name as keyof FormErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleRecaptchaChange = (token: string | null) => {
    setRecaptchaToken(token);
    setRecaptchaError(null);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!recaptchaToken) {
      setRecaptchaError('Potwierdź, że nie jesteś robotem');
      return;
    }

    const errors: FormErrors = {};
    let hasErrors = false;

    if (!formData.firstName.trim()) {
      errors.firstName = 'Imię jest wymagane';
      hasErrors = true;
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Nazwisko jest wymagane';
      hasErrors = true;
    }
    if (!formData.email.trim()) {
      errors.email = 'Adres email jest wymagany';
      hasErrors = true;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Podaj prawidłowy adres email (brak znaku @ lub domeny)';
      hasErrors = true;
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Numer telefonu jest wymagany';
      hasErrors = true;
    } else if (!/^[0-9\s+()-]{9,15}$/.test(formData.phone.replace(/\s+/g, ''))) {
      errors.phone = 'Podaj prawidłowy numer telefonu (minimum 9 cyfr)';
      hasErrors = true;
    }
    if (!formData.rodoConsent) {
      errors.rodoConsent = 'Potwierdzenie zapoznania się z klauzulą informacyjną RODO jest wymagane';
      hasErrors = true;
    }

    if (hasErrors) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await fetch(getApiUrl('courseRegistration'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getCacheHeaders() },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          courseId: eventId?.toString() || '',
          courseTitle: title || '',
          startDate: startDate || '',
          endDate: endDate || '',
          time,
          price,
          rodoAccepted: formData.rodoConsent,
          recaptchaToken,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSubmitSuccess(true);
        setFormData({ firstName: '', lastName: '', email: '', phone: '', rodoConsent: false });
        setRecaptchaToken(null);
        resetEnterpriseCaptcha();
      } else {
        setSubmitError(data.error || 'Wystąpił błąd podczas wysyłania formularza. Spróbuj ponownie później.');
      }
    } catch (error) {
      console.error('Błąd podczas wysyłania formularza:', error);
      setSubmitError('Wystąpił nieoczekiwany błąd. Prosimy spróbować później lub skontaktować się z nami telefonicznie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isPast = !!(startDate && new Date(startDate) < new Date());

  return (
    <>
      <div className="mt-12 bg-[rgb(6,40,95)]/5 p-8 rounded-xl" aria-labelledby="registration-heading">
        {!submitSuccess && (
          <h2 className="text-2xl font-semibold mb-6 text-center" id="registration-heading">
            Zapisz się na to szkolenie
          </h2>
        )}

        {isPast ? (
          <div className="bg-gray-50 border border-gray-200 text-gray-700 p-6 rounded-xl text-center" role="alert" aria-live="polite">
            <h3 className="text-xl font-bold mb-3">Kurs już się odbył</h3>
            <p className="mb-4">Ten kurs już się zakończył. Zachęcamy do sprawdzenia innych terminów szkoleń.</p>
            <div className="mt-4 flex justify-center space-x-4">
              <a href="/training" className="bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 text-white py-2 px-4 rounded" aria-label="Przejdź do listy dostępnych szkoleń">
                Zobacz inne terminy
              </a>
              <a href="/contact" className="bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 text-white py-2 px-4 rounded" aria-label="Przejdź do strony kontaktowej">
                Skontaktuj się z nami
              </a>
            </div>
          </div>
        ) : !hasSeats ? (
          <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-xl text-center" role="alert" aria-live="polite">
            <h3 className="text-xl font-bold mb-3">Brak wolnych miejsc</h3>
            <p className="mb-4">
              Niestety, na ten termin szkolenia nie ma już wolnych miejsc. Zachęcamy do sprawdzenia innych terminów lub skontaktowania się z nami.
            </p>
            <div className="mt-4 flex justify-center space-x-4">
              <a href="/training" className="bg-red-600 hover:bg-red-700 text-white py-2 px-4 rounded" aria-label="Przejdź do listy dostępnych szkoleń">
                Zobacz inne terminy
              </a>
              <a href="/contact" className="bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 text-white py-2 px-4 rounded" aria-label="Przejdź do strony kontaktowej">
                Skontaktuj się z nami
              </a>
            </div>
          </div>
        ) : submitSuccess ? (
          <div className="bg-green-50 border border-green-200 text-green-700 p-6 rounded-xl text-center" role="alert" aria-live="polite">
            <h3 className="text-xl font-bold mb-3">Dziękujemy za zapisanie się!</h3>
            <p className="mb-4">
              Twoje zgłoszenie zostało przyjęte. Na podany adres email wysłaliśmy potwierdzenie.<br />
              W najbliższym czasie skontaktujemy się z Tobą za pomocą email, aby przekazać szczegółowe informacje.
            </p>
            <button onClick={() => setSubmitSuccess(false)} className="mt-4 bg-green-700 hover:bg-green-800 text-white py-2 px-4 rounded" aria-label="Wypełnij formularz ponownie">
              Zapisz się ponownie
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6" aria-labelledby="registration-heading">
            {submitError && (
              <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded" role="alert" aria-live="assertive">
                {submitError}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-gray-700 font-medium mb-2">
                  Imię <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only">wymagane</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text" id="firstName" name="firstName" value={formData.firstName} onChange={handleInputChange} autoComplete="given-name"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-[rgb(6,40,95)] ${formErrors.firstName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Twoje imię" aria-required="true" aria-invalid={formErrors.firstName ? 'true' : 'false'} aria-describedby={formErrors.firstName ? 'firstName-error' : undefined}
                  />
                </div>
                {formErrors.firstName && <p className="mt-1 text-red-500 text-sm" id="firstName-error" role="alert">{formErrors.firstName}</p>}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-gray-700 font-medium mb-2">
                  Nazwisko <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only">wymagane</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="text" id="lastName" name="lastName" value={formData.lastName} onChange={handleInputChange} autoComplete="family-name"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-[rgb(6,40,95)] ${formErrors.lastName ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Twoje nazwisko" aria-required="true" aria-invalid={formErrors.lastName ? 'true' : 'false'} aria-describedby={formErrors.lastName ? 'lastName-error' : undefined}
                  />
                </div>
                {formErrors.lastName && <p className="mt-1 text-red-500 text-sm" id="lastName-error" role="alert">{formErrors.lastName}</p>}
              </div>

              <div>
                <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                  E-mail <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only">wymagane</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaEnvelope className="text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="email" id="email" name="email" value={formData.email} onChange={handleInputChange} autoComplete="email"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-[rgb(6,40,95)] ${formErrors.email ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="twoj@email.com" aria-required="true" aria-invalid={formErrors.email ? 'true' : 'false'} aria-describedby={formErrors.email ? 'email-error' : undefined}
                  />
                </div>
                {formErrors.email && <p className="mt-1 text-red-500 text-sm" id="email-error" role="alert">{formErrors.email}</p>}
              </div>

              <div>
                <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                  Telefon <span className="text-red-600" aria-hidden="true">*</span><span className="sr-only">wymagane</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaPhone className="text-gray-400" aria-hidden="true" />
                  </div>
                  <input
                    type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} autoComplete="tel"
                    className={`w-full pl-10 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-[rgb(6,40,95)] ${formErrors.phone ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Numer telefonu" aria-required="true" aria-invalid={formErrors.phone ? 'true' : 'false'} aria-describedby={formErrors.phone ? 'phone-error' : undefined}
                  />
                </div>
                {formErrors.phone && <p className="mt-1 text-red-500 text-sm" id="phone-error" role="alert">{formErrors.phone}</p>}
              </div>
            </div>

            {RECAPTCHA_CONFIG.SITE_KEY && (
              <div className="flex flex-col items-center justify-center mt-4" aria-live="polite">
                <div aria-label="Weryfikacja CAPTCHA - potwierdź, że nie jesteś robotem">
                  <ReCaptchaEnterprise sitekey={RECAPTCHA_CONFIG.SITE_KEY} action="SUBMIT" onChange={handleRecaptchaChange} />
                </div>
                {recaptchaError && <p className="mt-2 text-red-500 text-sm" id="recaptcha-error" role="alert">{recaptchaError}</p>}
              </div>
            )}

            <div className="mt-4">
              <div className="flex items-start space-x-3">
                <div className="flex items-center h-5">
                  <input
                    id="rodoConsent" name="rodoConsent" type="checkbox" checked={formData.rodoConsent} onChange={handleInputChange} autoComplete="off"
                    className={`h-5 w-5 rounded border-2 text-[rgb(6,40,95)] focus:ring-2 focus:ring-[rgb(6,40,95)] focus:ring-offset-2 transition-colors ${formErrors.rodoConsent ? 'border-red-500' : 'border-gray-400'}`}
                    required aria-required="true" aria-invalid={formErrors.rodoConsent ? 'true' : 'false'} aria-describedby={formErrors.rodoConsent ? 'rodo-error' : undefined}
                  />
                </div>
                <div className="text-sm">
                  <label htmlFor="rodoConsent" className="font-medium text-gray-700 leading-relaxed cursor-pointer">
                    Potwierdzam, że zapoznałem/am się z klauzulą informacyjną dotyczącą przetwarzania moich danych osobowych
                    <button type="button" onClick={() => setShowRodoDetails(true)} className="ml-1 text-[rgb(6,40,95)] underline hover:text-[rgb(6,40,95)]/80 focus:outline-none focus:ring-2 focus:ring-[rgb(6,40,95)] rounded" aria-label="Pokaż szczegóły klauzuli RODO">
                      (zobacz pełną treść)
                    </button>
                    .
                    <span className="block mt-1 text-xs text-gray-500 font-normal">
                      Państwa dane będą przetwarzane w celu obsługi zgłoszenia oraz realizacji umowy szkoleniowej na podstawie art. 6 ust. 1 lit. b i c RODO. Pełna klauzula dostępna jest na stronie <a href="/rodo" target="_blank" rel="noopener noreferrer" className="underline text-[rgb(6,40,95)]">Polityki Prywatności</a>.
                    </span>
                  </label>
                  {formErrors.rodoConsent && <p className="mt-1 text-red-500 text-sm" id="rodo-error" role="alert">{formErrors.rodoConsent}</p>}
                </div>
              </div>
            </div>

            <div className="text-center pt-4">
              <button
                type="submit" disabled={isSubmitting || !recaptchaToken}
                className={`bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 text-white font-medium py-3 px-8 rounded-lg ${isSubmitting || !recaptchaToken ? 'opacity-70 cursor-not-allowed' : ''}`}
                aria-disabled={isSubmitting || !recaptchaToken} aria-live="polite"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Zapisz się'}
              </button>
            </div>
          </form>
        )}
      </div>

      {showRodoDetails && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4 overflow-y-auto" role="dialog" aria-modal="true" aria-labelledby="rodo-modal-title">
          <div className="bg-white rounded-lg shadow-xl p-6 md:p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h2 className="text-xl font-bold text-[rgb(6,40,95)]" id="rodo-modal-title">{rodoContent?.title || 'RODO – Klauzula informacyjna'}</h2>
              <button onClick={() => setShowRodoDetails(false)} className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-[rgb(6,40,95)] focus:border-[rgb(6,40,95)] rounded" aria-label="Zamknij okno klauzuli RODO">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: rodoContent?.content || '' }} />
            <div className="mt-6 flex justify-end">
              <button onClick={() => setShowRodoDetails(false)} className="px-4 py-2 bg-[rgb(6,40,95)] hover:bg-[rgb(6,40,95)]/90 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(6,40,95)]" aria-label="Zamknij okno klauzuli RODO">
                Zamknij
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
